import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

// Dispatched on document once the page's other sections show a newly loaded (combined-listing) product.
const SECTIONS_REFRESHED_EVENT = 'product:sections-refreshed';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #sectionsAbortController;

  /**
   * The product the page's other sections show, when it can differ from the product section's: a background
   * refresh cut short by a newer switch leaves them on an older product. Undefined when they match.
   * @type {string | undefined}
   */
  #otherSectionsProductId;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Combined-listing options load a different product: morph this whole product section for it. Other
    // product-dependent sections are refreshed afterwards in the background (see #refreshOtherSections).
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    // A change made while a product switch is still loading is requested against that new product, so it completes the switch.
    const hasPendingProductSwitch =
      isOnProductPage && !!this.#pendingRequestUrl && this.#pendingRequestUrl.split('?')[0] !== currentUrl;
    const isProductSwitch = (loadsNewProduct || hasPendingProductSwitch) && !isOnFeaturedProductSection;
    const { sectionId } = this.dataset;

    const morphElementSelector = isOnFeaturedProductSection
      ? 'featured-product-information'
      : isProductSwitch
      ? sectionId
        ? `#shopify-section-${sectionId}`
        : 'main'
      : undefined;

    const isUnavailableOption =
      selectedOption instanceof HTMLInputElement && selectedOption.dataset.optionAvailable === 'false';

    // Product-page variant changes (colour and size) only render this section, never the full page, to keep the
    // request small. The section id comes from the live page, so each arm of a template A/B test keeps its own template.
    let requestUrl = this.buildRequestUrl(selectedOption);
    if (isOnProductPage && sectionId && !requestUrl.includes('section_id=')) {
      requestUrl += `&section_id=${sectionId}`;
    }

    this.fetchUpdatedSection(requestUrl, morphElementSelector, isUnavailableOption, isProductSwitch, isOnProductPage);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.

    // Old Code
    // const isUnavailable = selectedOption instanceof HTMLElement && selectedOption.dataset.optionAvailable === 'false';
    // const connectedProductUrl = isUnavailable ? undefined : selectedOption.dataset.connectedProductUrl;

    // New Code - fetch the correct connected product URL
    const connectedProductUrl = selectedOption.dataset.connectedProductUrl;

    let productUrl = connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
      'dialog-component[data-section-id]': this.closest('dialog-component[data-section-id]')?.dataset.sectionId,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {boolean} [skipVariantPickerMorph] - When true, skips morphing the variant picker to avoid corrupting product state for unavailable combined-listing options.
   * @param {boolean} [isProductSwitch] - When true, the response is a different product (combined listing) replacing the product section.
   * @param {boolean} [isOnProductPage] - When true, a request that fails or returns something unusable falls back to a
   *   normal page load, so the page is never left half-switched with add to cart waiting for a variant:update.
   */
  fetchUpdatedSection(
    requestUrl,
    morphElementSelector,
    skipVariantPickerMorph = false,
    isProductSwitch = false,
    isOnProductPage = false
  ) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => (response.ok ? response.text() : ''))
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          // An error or bot-check page, or a product whose template has no picker: load the page normally.
          if (isOnProductPage) this.#loadPage(requestUrl);
          return;
        }

        let newProduct;
        let mainMorphed = false;
        const previousProductId = this.dataset.productId;
        const { sectionId } = this.dataset;
        // The product this response shows; read before the morph moves nodes out of the parsed document.
        const responseProductUrl = html.querySelector('variant-picker')?.dataset.productUrl;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
          mainMorphed = true;
          this.#otherSectionsProductId = undefined;
          this.#applyProductData(document.querySelector('main'));
          document.dispatchEvent(new CustomEvent(SECTIONS_REFRESHED_EVENT));
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
          if (isProductSwitch) {
            mainMorphed = true;
            this.#applyProductData(
              sectionId ? document.getElementById(`shopify-section-${sectionId}`) : this.closest(morphElementSelector)
            );
            this.#refreshOtherSections(requestUrl.split('?')[0], previousProductId);
          }
        } else if (!skipVariantPickerMorph) {
          newProduct = this.updateVariantPicker(html);
        }

        const variant = JSON.parse(textContent);

        // The address bar was updated from the clicked input before the response came. A click made while a
        // product switch was still loading can leave it naming the wrong product (a size picked on the old
        // product's picker, or the original colour picked again), so match it to what the response shows.
        if (isOnProductPage) {
          const url = new URL(window.location.href);
          if (isProductSwitch && responseProductUrl) url.pathname = responseProductUrl.split('?')[0];
          if (variant?.id) url.searchParams.set('variant', String(variant.id));
          if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress. If the morph
        // replaced this picker (an ancestor rendered differently), dispatch from the live one so the event
        // still reaches the form.
        const source = (!this.isConnected && sectionId && this.#livePicker(sectionId)) || this;
        if (source.selectedOptionId) {
          source.dispatchEvent(
            new VariantUpdateEvent(variant, source.selectedOptionId, {
              html,
              productId: source.dataset.productId ?? '',
              newProduct,
              mainMorphed,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
          return;
        }

        console.error(error);
        this.#pendingRequestUrl = undefined;
        // A network error, or a response that could not be applied: load the page normally.
        if (isOnProductPage) this.#loadPage(requestUrl);
      });
  }

  /**
   * Loads the requested product page normally (without section_id). Used when a product-page variant request
   * fails, so the page never stays half-switched with add to cart waiting for a variant:update.
   * @param {string} requestUrl - The section request URL.
   */
  #loadPage(requestUrl) {
    const request = new URL(requestUrl, window.location.origin);
    // Keep the address bar's other parameters (UTM, gclid, …); take the product, option_values and view from the request.
    const pageUrl = new URL(window.location.href);
    pageUrl.pathname = request.pathname;
    for (const key of ['option_values', 'view']) {
      const value = request.searchParams.get(key);
      if (value) {
        pageUrl.searchParams.set(key, value);
      } else {
        pageUrl.searchParams.delete(key);
      }
    }
    if (pageUrl.searchParams.has('option_values')) pageUrl.searchParams.delete('variant');
    pageUrl.searchParams.delete('section_id');
    window.location.replace(pageUrl.toString());
  }

  /**
   * The picker that is in the page now, in the given product section.
   * @param {string} sectionId
   * @returns {VariantPicker | null}
   */
  #livePicker(sectionId) {
    const picker = document.querySelector(`#shopify-section-${sectionId} variant-picker`);
    return picker instanceof VariantPicker ? picker : null;
  }

  /**
   * Applies the new product's page data after a combined-listing switch. The product section carries it as inert
   * JSON (snippets/product-tracking-data.liquid, rendered by sections/product-information.liquid), because a
   * section-only response has no <head>: the Meta and Klaviyo product objects and the browser tab title.
   * @param {Element | null} container - The morphed product section (or <main>).
   */
  #applyProductData(container) {
    const data = container?.querySelector('script[data-product-tracking-data]')?.textContent;
    if (!data) return;

    try {
      const { fb, kl, title } = JSON.parse(data);
      if (fb) window.__fbProduct = fb;
      if (kl) window.__klProduct = kl;
      if (title) {
        // page_title can arrive HTML-escaped (for example "&amp;"); decode it as text.
        const decoder = document.createElement('textarea');
        decoder.innerHTML = title;
        document.title = decoder.value;
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
    // Hydrate lazy images injected by the morph. Use rAF so we run after
    // component updatedCallback microtasks have had a chance to settle.
    requestAnimationFrame(() => {
      if (typeof window.runLazyImages === 'function') window.runLazyImages();
    });
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
    // Hydrate lazy images injected by the morph. Use rAF so we run after
    // component updatedCallback microtasks have had a chance to settle.
    requestAnimationFrame(() => {
      if (typeof window.runLazyImages === 'function') window.runLazyImages();
    });
  }

  /**
   * Re-renders, in the background, the page's other sections that show the previous product, so the product
   * section swap itself stays a single section-only request. A section is refreshed when its HTML contains the
   * previous product's id (for example the Klaviyo reviews block's data-id), or when it holds an element marked
   * data-refresh-on-product-switch. Static sections (sliders, banners, FAQs) are left alone.
   * @param {string} productUrl - The new product's URL.
   * @param {string} [previousProductId] - The id of the product the page showed before the switch.
   */
  #refreshOtherSections(productUrl, previousProductId) {
    this.#sectionsAbortController?.abort();
    this.#sectionsAbortController = new AbortController();
    const { signal } = this.#sectionsAbortController;

    const productSectionId = `shopify-section-${this.dataset.sectionId}`;
    // A background refresh cut short by a newer switch leaves the other sections on an older product than the
    // one the product section just left, so look for both ids.
    const staleProductIds = [this.#otherSectionsProductId, previousProductId].filter((id) => !!id);
    const sectionIds = Array.from(document.querySelectorAll('main .shopify-section'))
      .filter((section) => section.id.startsWith('shopify-section-') && section.id !== productSectionId)
      .filter(
        (section) =>
          section.querySelector('[data-refresh-on-product-switch]') ||
          staleProductIds.some((id) => section.innerHTML.includes(id))
      )
      .map((section) => section.id.replace('shopify-section-', ''));
    if (this.#otherSectionsProductId === undefined) this.#otherSectionsProductId = previousProductId;

    const requests = [];

    // Carry the view parameter through, so an alternate-template (?view=) A/B test keeps
    // rendering its own template when the background section refresh runs.
    const viewParamValue = getViewParameterValue();
    const viewParam = viewParamValue ? `&view=${encodeURIComponent(viewParamValue)}` : '';

    // The Section Rendering API accepts at most 5 sections per request.
    for (let i = 0; i < sectionIds.length; i += 5) {
      const batch = sectionIds.slice(i, i + 5);

      requests.push(
        fetch(`${productUrl}?sections=${batch.join(',')}${viewParam}`, { signal })
          .then((response) => (response.ok ? response.json() : {}))
          .then((sections) => {
            for (const id of batch) {
              const current = document.getElementById(`shopify-section-${id}`);
              const next =
                sections[id] &&
                new DOMParser().parseFromString(sections[id], 'text/html').getElementById(`shopify-section-${id}`);
              if (current && next) morph(current, next);
            }

            requestAnimationFrame(() => {
              if (typeof window.runLazyImages === 'function') window.runLazyImages();
            });
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          })
      );
    }

    Promise.all(requests).then(() => {
      if (signal.aborted) return;
      this.#otherSectionsProductId = undefined;
      document.dispatchEvent(new CustomEvent(SECTIONS_REFRESHED_EVENT));
    });
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
